import type { PayoutAccountReviewRow } from '@dayon/contracts';
import { documentWords } from '../identity/document-words.ts';
import { datedStamp } from './payout-state.ts';

/**
 * WHAT THE PAYOUT ACCOUNTS TAB SAYS ABOUT AN ACCOUNT'S TWO NAMES, ITS DOCUMENT AND ITS STANDING.
 *
 * Kept apart from `PayoutAccountsReview.tsx` so the rules can be tested without rendering a
 * 700-line tab, the way `payout-state.ts` is kept apart from the funds screen: which words of the
 * two names are set in bold, the name on the ID, what the document line reads, and the sentence
 * that says who decided an account and when.
 *
 * THE NAME SPLIT IS A COPY, AND A TEST HOLDS IT TO THE ORIGINAL. `nameTokens` is the platform's own
 * split of a name into the words it compares: the steps, in the same order, of `nameTokens` in
 * `services/api/src/modules/player-money/name-match.ts`, which `holderMatchesIdentity` uses to
 * decide MATCHED or MISMATCH when an account is registered. A console does not import a service's
 * source, so the steps are repeated here. A copy that drifted would set in bold words the platform
 * never compared, or leave plain words it did, and tell a reviewer where to look wrongly.
 * `payout-review-names.test.ts` runs this copy and the API's function over the API's own cases and
 * over probes read out of the API's source (every ending it drops, every apostrophe it joins), so
 * a change made there and not here fails there; and it checks that the holder's words left plain
 * are exactly the holders the API accepts, so the bold is held to the decision and not only to the
 * split under it.
 *
 * NOTHING HERE DECIDES A MATCH. The API did that when the account was registered; this only says
 * which words to look at, and the bold is a reading aid for a person, not a verdict.
 */

/** The endings that are not part of a name: dropped on both sides, as the API drops them. */
const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V']);
/* An apostrophe joins the letters either side of it (O'Neil is ONEIL), where a full stop or a hyphen
   separates them. The typographic ones are here because an ID reader returns whichever the card was
   printed with, and the player's keyboard types the plain one. */
const APOSTROPHES = /['\u2018\u2019\u02bc]/g;

/**
 * A name as the words the platform compares: accents off, capitals, apostrophes joined, anything
 * else that is not a letter a space, then the words, with the endings above dropped — so
 * "Ma. Lourdes Dela Cruz-Reyes Jr." is MA, LOURDES, DELA, CRUZ, REYES.
 */
export function nameTokens(name: string): string[] {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(APOSTROPHES, '')
    .replace(/[^A-Z\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t !== '' && !NAME_SUFFIXES.has(t));
}

/** One word of a name as it was printed, and whether the other name lacks it. */
export interface NameWord {
  word: string;
  differs: boolean;
}

/**
 * A name as it was printed, word by word, each marked when it is in only one of the two names.
 *
 * A word differs when any of the words it splits into is missing from the other name, so
 * "Cruz-Reyes" differs against "DELA CRUZ" and not against "DELA CRUZ REYES"; a word that splits
 * into nothing (a lone "Jr.") never does. Compared with nothing (`against` is null) no word
 * differs: there is no other name to point at.
 */
export function nameWords(name: string, against: string | null): NameWord[] {
  const other = new Set(against === null ? [] : nameTokens(against));
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => ({
      word,
      differs: against !== null && nameTokens(word).some((t) => !other.has(t)),
    }));
}

/**
 * The name on the player's verified ID, or null when the read carries none (a blank is no name).
 *
 * The null is what keeps a missing name from being compared: against an empty name every word of
 * the other would differ, and the drawer would set a whole name in bold under the line that says
 * there is nothing to compare it with. The name is returned as it was read, not trimmed.
 */
export const idNameOf = (identityFullName: string | null): string | null =>
  identityFullName !== null && identityFullName.trim() !== '' ? identityFullName : null;

/**
 * What the ID's lines read for a reader the API did not send the player's ID to (`identityShown`
 * is false): the name on the ID and the document it was read from. Withheld is not absent — the
 * player may well have an ID on record, and "none on record" would say the platform holds none.
 */
export const IDENTITY_WITHHELD = 'shown to reviewers only';

/**
 * The document the ID name was read from: its type in the words Identity checks uses for it
 * (`documentWords`, the one map both screens read — an unknown type is shown as received, its
 * underscores opened out, not mapped to a guess), and the last four of its number, which is all the
 * API sends. Null when the read carries neither.
 */
export function documentOf(
  row: Pick<PayoutAccountReviewRow, 'identityDocumentType' | 'identityDocumentNumberMask'>,
): string | null {
  const parts = [documentWords(row.identityDocumentType), row.identityDocumentNumberMask].filter(
    (part): part is string => typeof part === 'string' && part !== '',
  );
  return parts.length === 0 ? null : parts.join(' ');
}

/**
 * Who decided an account, and when, in a sentence — from what the row carries and nothing else.
 *
 * An account whose names matched is accepted by the platform and leaves no reviewer on the row, and
 * the player may retire an account nobody had decided on yet; both are said as what they are
 * rather than as a verdict nobody gave. A refusal can never be retired (the API refuses it), so a
 * retired account that carries a reviewer carries an acceptance.
 */
export function decisionOf(
  row: Pick<PayoutAccountReviewRow, 'status' | 'nameMatch' | 'verifiedAt' | 'verifiedByLabel'>,
): string {
  const by = row.verifiedByLabel ?? 'a reviewer';
  const decidedAt = row.verifiedAt === null ? null : datedStamp.format(new Date(row.verifiedAt));
  const accepted =
    decidedAt !== null
      ? `accepted by ${by} · ${decidedAt}`
      : row.nameMatch === 'MATCHED'
        ? 'accepted automatically, the names having matched when it was registered'
        : null;
  switch (row.status) {
    case 'PENDING_REVIEW':
      return 'Not decided yet';
    case 'ACTIVE':
      return accepted === null ? 'Accepted' : `${accepted.charAt(0).toUpperCase()}${accepted.slice(1)}`;
    case 'REFUSED':
      return decidedAt === null ? 'Refused' : `Refused by ${by} · ${decidedAt}`;
    case 'RETIRED':
      return accepted === null
        ? 'Retired before anyone decided on it'
        : `Retired — it had been ${accepted}`;
  }
}
